import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MedOptionType } from "@mk/med-ui";
import { catchError } from "rxjs";
import { RecipientSeriesController } from "@app/ui_controllers/recipientSeriesController";
import { DistributionPageDto, DistributionRegionRecipientDto } from "@app/ui_models/web-response";
import { DistributionGoodDto, SeriesSpecificationDto } from "@app/ui_models/model-dto";
import { DistributionController } from "@app/ui_controllers/distributionController";
import { MedNotificationService } from "mk/med-notification";

@Component({
  selector: "app-series",
  templateUrl: "./series.component.html",
  styleUrls: ["/series.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SeriesComponent implements OnInit {

  constructor(private router: Router,
              private route: ActivatedRoute,
              private  recipientSeriesController: RecipientSeriesController,
              private readonly distributionController: DistributionController,
              private readonly notificationService: MedNotificationService) {
  }

  optionsTabGroup: MedOptionType<DistributionGoodDto>[] = [];
  tabHeader: { label: string; key: string;  }[] = tabHeaderSeries;
  distribution: DistributionPageDto | null = null;
  distributionRegions: DistributionRegionRecipientDto[] | undefined = [];
  activeDistributionRegions: DistributionRegionRecipientDto[] = [];
  currentRegionId: number | null = null;
  activeIndex: any = -1;
  distributionId = 0;
  addSeriesModal = false;
  allSeries: SeriesSpecificationDto[] = [];
  selectedSeries: SeriesSpecificationDto[] = [];
  activeSelectedSeries: SeriesSpecificationDto[] = [];
  quantitiesToUpdate: {id: number; quantity: number; }[] = [];
  activeDistributionGoodId: number | undefined | null = 0;
  optionsModel: DistributionGoodDto | null = null;
  protected readonly alert = alert;

  ngOnInit(): void {
    const routeId = this.route.snapshot.paramMap.get("id");
    if (!routeId) {
      return;
    }

    this.distributionId = Number(routeId);

    this.distributionController.getDistribution(this.distributionId).subscribe(data => {
      this.distribution = data["distribution"] as DistributionPageDto;

      this.activeDistributionGoodId = this.distribution.distributionGoods[0].distributionGoodsId;
      this.distributionRegions = this.distribution.regionRecipients;
      this.activeDistributionRegions = this.getActiveDistributionRegions();
      this.distribution.distributionGoods.forEach((distr: DistributionGoodDto) => {
        this.optionsTabGroup.push({ value: distr, label: distr.mnnName });
      });
      this.optionsModel = this.optionsTabGroup[0].value;
      this.getInitialAllSeries();
    });

  }

  getInitialAllSeries() {
    if (this.activeDistributionGoodId) {
      this.recipientSeriesController.getDistributionSeries(this.activeDistributionGoodId).subscribe(data => {
        this.allSeries = data;
      });
    }
  }

  getDistributionRegions(distributionId: number) {
    this.distributionController.getDistribution(distributionId).subscribe(data => {
      this.distribution = data["distribution"] as DistributionPageDto;
      this.distributionRegions = this.distribution.regionRecipients;
      this.activeDistributionRegions = this.getActiveDistributionRegions();

    });
  }

  getActiveDistributionRegions(): DistributionRegionRecipientDto[] {
    if (!this.distributionRegions) {
      return [];
    }
    return this.distributionRegions.filter(
      (item: DistributionRegionRecipientDto) =>
        item.distributionGoodsId === this.activeDistributionGoodId,
    );
  }

  goToDistribution() {
    this.router.navigate(["/distribution/view/" + this.distributionId]);
  }
  tabGroupChanged($event: DistributionGoodDto | null): void {
    this.activeDistributionGoodId = $event?.distributionGoodsId;
    this.getDistributionRegions(this.distributionId);
    this.loadSelectedSeries(this.currentRegionId);
    this.currentRegionId = null;
    this.activeIndex = -1;
    if (this.activeDistributionGoodId) {
      this.recipientSeriesController.getDistributionSeries(this.activeDistributionGoodId).subscribe(data => {
        this.allSeries = data;
      });
    }
  }

  getHeaderClass(index: number): string {
    let className = "";
    if (index === this.activeIndex) {
      className = "purple-10-background";
    }
    return className;
  }

  getCurrentRegionName(): string {
    const region = this.activeDistributionRegions?.find(region => region.id === this.currentRegionId);
    return region ? region.regionName : "";
  }

  loadSelectedSeries(regionId: number | null) {
    if (!regionId) {
      return;
    }
    this.recipientSeriesController.getDistributionSeriesByRegion(regionId).subscribe(data => {
      this.selectedSeries = data;
    });
  }

  openAddSeriesModal(regionId: number | null) {
    this.addSeriesModal = true;
    this.currentRegionId = regionId;
    if (this.selectedSeries) {
      this.activeSelectedSeries = this.allSeries.filter(series =>
        this.selectedSeries.some(s => s.seriesCatalogue === series.seriesCatalogue),
      );
      this.activeSelectedSeries.forEach(series => {
        const match = this.selectedSeries.find(s => s.seriesCatalogue === series.seriesCatalogue);
        if (match) {
          (series as any).quantity = match.quantity ?? 0;
          (series as any).distributionRecipientSeriesId = match.distributionRecipientSeriesId ?? -1;
        }
      });
    }
  }

  onAccordionOpen(event: any): void {
    this.quantitiesToUpdate = [];
    const { index } = event;
    this.activeIndex = index;
    const region = this.activeDistributionRegions?.[index];
    if (region?.id) {
      this.currentRegionId = region?.id;
      if (this.currentRegionId) {
        this.loadSelectedSeries(this.currentRegionId);
      }
    }
  }

  onQuantityChange(id: number, quantity: number): void {
    const existing = this.quantitiesToUpdate.find(quantity => quantity.id === id);
    if (existing) {
      existing.quantity = quantity;
    } else {
      this.quantitiesToUpdate.push({ id, quantity });
    }
  }

  updateDistributedQunatity() {
    const newQuantity = this.selectedSeries.reduce((sum, item) => sum + Number(item.quantity), 0);
    const currentRegion = this.activeDistributionRegions.find(item => item.id === this.currentRegionId);
    if (currentRegion) {
      currentRegion.distributedSeriesQuantity = newQuantity;
    }
  }

  accept(): void {
    const seriesByRegion = this.activeSelectedSeries.map(item => ({
      seriesSpecificationId: item.id,
    }));
    if (this.currentRegionId) {
      this.recipientSeriesController.createSeriesByRegion(this.currentRegionId, seriesByRegion)
        .pipe(
          catchError(response => {
            this.notificationService.error("Помилка при збереженні серій",
            );
            throw response;
          }),
        )
        .subscribe(() => {
          this.addSeriesModal = false;
          this.loadSelectedSeries(this.currentRegionId);
        });
    }
  }

  update(): void {
    if (this.quantitiesToUpdate.length > 0) {
      this.recipientSeriesController.updateQuntity(this.quantitiesToUpdate)
        .pipe(
          catchError(response => {
            this.notificationService.error("Помилка при зміні кількості",
            );
            throw response;
          }),
        )
        .subscribe(() => {
          this.notificationService.success("Кількість успішно змінено");
          this.updateDistributedQunatity();
        });
    }
  }

  deleteSeries(id: number | null): void {
    if (id === null) {
      return;
    }
    this.recipientSeriesController.deleteSeries(id)
      .pipe(
        catchError(response => {
          this.notificationService.error("Помилка при видаленні");
          throw response;
        }),
      )
      .subscribe(() => {
        this.notificationService.success("Успішно видалено");
        this.selectedSeries = this.selectedSeries.filter(
          series => series.distributionRecipientSeriesId !== id,
        );
        this.updateDistributedQunatity();
      });
  }
}

export const tabHeaderSeries = [
  { key: "1", label: "Серія/Каталожний номер" },
  { key: "2", label: "Дата виробництва" },
  { key: "3", label: "Термін придатності" },
  { key: "4", label: "Загальна кількість" },
  { key: "5", label: "Зарезервована кількість" },
  { key: "6", label: "Доступна кількість" },
  { key: "7", label: "Кількість серій" },
  { key: "8", label: "Розподілити" },
  { key: "9", label: "" },
];
